import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import Company from "@mapmycustomers/shared/types/entity/Company";
import User from "@mapmycustomers/shared/types/User";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import { showGlobalCreateCompanyModal } from "@app/component/createEditEntity/CreateCompanyModal/store/actions";
import DashCard from "@app/component/DashCard";
import CardCta from "@app/component/DashCard/CardCta";
import SelectField from "@app/component/input/SelectField";
import SimpleEntityMap from "@app/component/map/SimpleEntityMap";
import { getRecentCompaniesData, isRecentCompaniesDataLoading } from "@app/scene/home/store";
import { fetchRecentCompanies } from "@app/scene/home/store/actions";
import analytics from "@app/scene/reports/reportsAnalytics";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import sameLabelValueMapper from "@app/util/hook/mapper/sameLabelValueMapper";

import styles from "./RecentCompanies.module.scss";

const limitOptions = [5, 10, 25, 50, 100].map(sameLabelValueMapper);

interface Props {
  className?: string;
  companies: Company[];
  fetchRecentCompanies: typeof fetchRecentCompanies.request;
  hasCompanies: boolean;
  isLoading: boolean;
  onShowCreateCompanyModal: () => void;
  onShowEntityView: typeof showEntityView;
  selectedUserIds: User["id"][];
}

const RecentCompaniesCard: React.FC<Props> = ({
  className,
  companies,
  fetchRecentCompanies,
  hasCompanies,
  isLoading,
  onShowCreateCompanyModal,
  onShowEntityView,
  selectedUserIds,
}) => {
  const intl = useIntl();
  const [limit, setLimit] = useState(10);
  const handleChangeLimit = useCallback((limit: number) => setLimit(limit), [setLimit]);

  useEffect(() => {
    fetchRecentCompanies({
      limit,
      usersIds: selectedUserIds,
    });
  }, [fetchRecentCompanies, selectedUserIds, limit]);

  useChangeTracking(() => {
    analytics.clicked(["Recently Added Companies", "Select User"], {
      users: selectedUserIds,
    });
  }, [selectedUserIds]);

  const handleChangeBasemap = useCallback(
    (value: string) => analytics.clicked(["Recently Added Companies", "Change Basemap"], { value }),
    []
  );

  const handleSelectPin = useCallback(
    (company: Company) => analytics.clicked(["Recently Added Companies", "Pin"], { company }),
    []
  );

  useChangeTracking(() => {
    analytics.clicked(["Recently Added Companies", "Limit"], { limit });
  }, [limit]);

  const handlePreviewClick = useCallback(
    (company: Company) =>
      onShowEntityView({ entityId: company.id, entityType: EntityType.COMPANY }),
    [onShowEntityView]
  );

  return (
    <DashCard
      actions={[
        <SelectField<number>
          className={styles.limitField}
          dropdownMatchSelectWidth={false}
          key="limit"
          label={intl.formatMessage({
            id: "home.cards.recentCompanies.selectField.label",
            defaultMessage: "Limit",
            description: "Label for select field in Recent Companies Card",
          })}
          labelClassName={styles.filterLabel}
          onChange={handleChangeLimit}
          options={limitOptions}
          value={limit}
        />,
      ]}
      className={className}
      title={intl.formatMessage({
        id: "home.cards.recentCompanies.title",
        defaultMessage: "Recently Added Companies",
        description: "Recent Companies Card Title",
      })}
    >
      {!isLoading && !companies.length && !hasCompanies && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.recentCompanies.cta.addCompany",
            defaultMessage: "Add Company",
            description: "Recent Companies Card CTA button label",
          })}
          onClick={() => onShowCreateCompanyModal()}
          text={intl.formatMessage({
            id: "home.cards.recentCompanies.cta.noCompaniesFound",
            defaultMessage: "No recent companies found. Add a company or change filters.",
            description: "Recent Companies Card CTA text for no companies found",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050258093-Companies-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.recentCompanies.cta.learnMore"
              defaultMessage="Learn more about companies"
              description="Recent Companies Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      {!isLoading && !companies.length && hasCompanies && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.recentCompanies.cta.addCompany",
            defaultMessage: "Add Company",
            description: "Recent Companies Card CTA button label",
          })}
          onClick={() => onShowCreateCompanyModal()}
          text={intl.formatMessage({
            id: "home.cards.recentCompanies.cta.noCompaniesFound",
            defaultMessage: "No recent companies found. Add a company or change filters.",
            description: "Recent Companies Card CTA text for no companies found",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050258093-Companies-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.recentCompanies.cta.learnMore"
              defaultMessage="Learn more about companies"
              description="Recent Companies Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      <section className={cn(styles.container, { [styles.visible]: companies.length > 0 })}>
        <SimpleEntityMap<Company>
          entities={companies}
          entityType={EntityType.COMPANY}
          loading={isLoading}
          onChangeBasemap={handleChangeBasemap}
          onPreview={handlePreviewClick}
          onSelectPin={handleSelectPin}
          showBaseLayerSelector
        />
      </section>
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  companies: getRecentCompaniesData(state),
  // TODO: fix me
  hasCompanies: true, //getHasCompanies(state),
  isLoading: isRecentCompaniesDataLoading(state),
});

const mapDispatchToProps = {
  fetchRecentCompanies: fetchRecentCompanies.request,
  onShowCreateCompanyModal: showGlobalCreateCompanyModal,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(RecentCompaniesCard);
